import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  FileDownload as FileDownloadIcon,
  Group as GroupIcon,
  ReceiptLong as ReceiptLongIcon,
  Paid as PaidIcon,
  TrendingUp as TrendingUpIcon,
  QueryStats as QueryStatsIcon,
  CalendarMonth as CalendarMonthIcon,
  History as HistoryIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { tryResolveTenantId } from '../utils/tenant';

function asDate(v) {
  if (!v) return null;
  if (typeof v?.toDate === 'function') return v.toDate();
  if (typeof v?._seconds === 'number') return new Date(v._seconds * 1000);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDate(value) {
  const d = asDate(value);
  if (!d) return '-';
  return d.toLocaleDateString('en-US', { timeZone: 'UTC' });
}

function formatHours(minutes) {
  const n = Number(minutes || 0);
  return (n / 60).toFixed(2);
}

function toFriendlyErrorMessage(error, fallback) {
  const code = String(error?.code || '').toLowerCase();
  const raw = String(error?.message || '').trim();

  if (code.includes('failed-precondition')) {
    return raw || 'Payroll cannot be approved yet. Resolve blocking issues first.';
  }
  if (code.includes('permission-denied')) {
    return 'You do not have permission to perform this payroll action.';
  }
  if (code.includes('unauthenticated')) {
    return 'Your session expired. Please sign in again.';
  }
  if (code.includes('internal') || raw.toUpperCase() === 'INTERNAL') {
    return 'Payroll service had an internal error. Please retry. If it continues, check Functions logs.';
  }
  return raw || fallback;
}

function StatTile({ icon, label, value, accent = '#2f6fdb' }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        background: `linear-gradient(180deg, ${accent}14 0%, #f7f9fb 60%)`,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 0.5 }}>
        <Box sx={{ color: accent, display: 'flex', alignItems: 'center' }}>{icon}</Box>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </Stack>
      <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>{value}</Typography>
    </Paper>
  );
}

export default function Payroll() {
  const [tenantId, setTenantId] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [switchingPeriod, setSwitchingPeriod] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeAction, setActiveAction] = useState(null);
  const [summary, setSummary] = useState(null);
  const [payPeriods, setPayPeriods] = useState([]);
  const [selectedPayPeriodId, setSelectedPayPeriodId] = useState('');
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const loadPayPeriods = useCallback(async (resolvedTenantId) => {
    const periodsQuery = query(
      collection(db, `tenants/${resolvedTenantId}/payPeriods`),
      orderBy('startDate', 'desc'),
      limit(30)
    );
    const snap = await getDocs(periodsQuery);
    setPayPeriods(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, []);

  const loadHistory = useCallback(async (resolvedTenantId) => {
    const historyQuery = query(
      collection(db, `tenants/${resolvedTenantId}/payPeriods`),
      where('status', 'in', ['approved', 'exported']),
      orderBy('startDate', 'desc'),
      limit(12)
    );
    const snap = await getDocs(historyQuery);
    setHistory(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  }, []);

  const loadSummary = useCallback(async (resolvedTenantId, payPeriodId = null) => {
    if (!functions) {
      throw new Error('Functions client is not initialized.');
    }
    const call = httpsCallable(functions, 'getPayrollSummary');
    const payload = payPeriodId ? { tenantId: resolvedTenantId, payPeriodId } : { tenantId: resolvedTenantId };
    const res = await call(payload);
    return res.data || null;
  }, []);

  const refresh = useCallback(async (
    resolvedTenantId,
    payPeriodId = null,
    includePeriodLists = false,
    options = {}
  ) => {
    const { showInitialLoader = false, showSwitchLoader = false } = options;
    const currentTenant = resolvedTenantId || tenantId;
    if (!currentTenant) return;
    if (showInitialLoader) setInitialLoading(true);
    if (showSwitchLoader) setSwitchingPeriod(true);
    setError('');
    try {
      const summaryPromise = loadSummary(currentTenant, payPeriodId);
      const metaPromises = includePeriodLists ? [loadHistory(currentTenant), loadPayPeriods(currentTenant)] : [];
      const [summaryData] = await Promise.all([summaryPromise, ...metaPromises]);
      setSummary(summaryData);
      if (!payPeriodId && summaryData?.payPeriod?.id) {
        setSelectedPayPeriodId((prev) => prev || summaryData.payPeriod.id);
      }
    } catch (e) {
      setError(e?.message || 'Could not load payroll data.');
    }
    if (showInitialLoader) setInitialLoading(false);
    if (showSwitchLoader) setSwitchingPeriod(false);
  }, [loadHistory, loadPayPeriods, loadSummary, tenantId]);

  useEffect(() => {
    (async () => {
      const resolvedTenantId = await tryResolveTenantId();
      if (!resolvedTenantId) {
        setError('Tenant could not be resolved.');
        setInitialLoading(false);
        return;
      }
      setTenantId(resolvedTenantId);
      await refresh(resolvedTenantId, null, true, { showInitialLoader: true });
    })();
  }, [refresh]);

  const handleSelectPayPeriod = useCallback(async (event) => {
    const nextPayPeriodId = event.target.value;
    setSelectedPayPeriodId(nextPayPeriodId);
    if (!tenantId || !nextPayPeriodId) return;
    if (summary?.payPeriod?.id === nextPayPeriodId) return;
    await refresh(tenantId, nextPayPeriodId, false, { showSwitchLoader: true });
  }, [refresh, summary?.payPeriod?.id, tenantId]);

  const payPeriod = summary?.payPeriod || null;
  const access = summary?.access || null;
  const payrollIssues = summary?.payrollIssues || [];
  const blockingIssues = payrollIssues.filter((i) => i?.severity === 'error');
  const canApprove = !!payPeriod && !['approved', 'exported'].includes(String(payPeriod.status || '').toLowerCase()) && blockingIssues.length === 0;
  const canExport = !!payPeriod
    && (payPeriod.status === 'approved' || payPeriod.status === 'exported')
    && (access?.canExport ?? true);

  const handleApprove = async () => {
    if (!tenantId || !payPeriod?.id || !functions) return;
    setActiveAction('approve');
    setSubmitting(true);
    setError('');
    setInfo('');
    try {
      const call = httpsCallable(functions, 'approvePayroll');
      const res = await call({ tenantId, payPeriodId: payPeriod.id });
      const data = res?.data || {};
      if (data.scopeRole === 'manager') {
        setInfo(`Location timecards approved (${data.approvedTimecards || 0}). Owner can finalize and export company payroll.`);
      } else {
        setInfo('Payroll approved. Timecards are now locked for this period.');
      }
      await refresh(tenantId, payPeriod.id, true);
    } catch (e) {
      setError(toFriendlyErrorMessage(e, 'Could not approve payroll.'));
    }
    setActiveAction(null);
    setSubmitting(false);
  };

  const handleExport = async (periodIdOverride = null) => {
    const normalizedOverride = typeof periodIdOverride === 'string' ? periodIdOverride : null;
    const targetPayPeriodId = normalizedOverride || payPeriod?.id || null;
    if (!tenantId || !targetPayPeriodId || !functions) return;
    setActiveAction('export');
    setSubmitting(true);
    setError('');
    setInfo('');
    try {
      const call = httpsCallable(functions, 'exportPayroll');
      const res = await call({ tenantId, payPeriodId: targetPayPeriodId });
      const data = res?.data || {};
      const fileName = data.fileName || `ShiftWorks_Payroll_${targetPayPeriodId}.csv`;
      const csvContent = String(data.csvContent || '');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
      setInfo('Payroll CSV exported successfully.');
      await refresh(tenantId, targetPayPeriodId, true);
    } catch (e) {
      setError(toFriendlyErrorMessage(e, 'Could not export payroll.'));
    }
    setActiveAction(null);
    setSubmitting(false);
  };

  const totals = useMemo(() => summary?.totals || {
    employeeCount: 0,
    timecardCount: 0,
    totalRegularMinutes: 0,
    totalOvertimeMinutes: 0,
    totalWorkedMinutes: 0,
  }, [summary]);

  const statTiles = [
    { icon: <GroupIcon fontSize="small" />, label: 'Employees', value: totals.employeeCount, accent: '#0b7285' },
    { icon: <ReceiptLongIcon fontSize="small" />, label: 'Timecards', value: totals.timecardCount, accent: '#6a4c93' },
    { icon: <PaidIcon fontSize="small" />, label: 'Regular Hours', value: formatHours(totals.totalRegularMinutes), accent: '#2f6fdb' },
    { icon: <TrendingUpIcon fontSize="small" />, label: 'Overtime Hours', value: formatHours(totals.totalOvertimeMinutes), accent: '#ef6c00' },
    { icon: <QueryStatsIcon fontSize="small" />, label: 'Total Hours', value: formatHours(totals.totalWorkedMinutes), accent: '#2e7d32' },
  ];

  if (initialLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress size={24} />
          <Typography>Loading payroll...</Typography>
        </Stack>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100%', backgroundColor: '#f8f9fb', pt: 2, pb: 4 }}>
      <Container
        maxWidth="xl"
        sx={{
          mt: 2,
          mb: 2,
          px: { xs: 1.5, md: 2.5 },
        }}
      >
      <Paper
        elevation={0}
        sx={{
          mb: 2.5,
          p: { xs: 2, md: 2.75 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(135deg, #eef3f9 0%, #f8fafc 45%, #eef6f0 100%)',
        }}
      >
        <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>Payroll</Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <CalendarMonthIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              {payPeriod ? (
                <Typography color="text.secondary" sx={{ fontWeight: 500 }}>
                  Current pay period: {formatDate(payPeriod.startDate)} - {formatDate(payPeriod.endDate)}
                </Typography>
              ) : (
                <Typography color="text.secondary" sx={{ fontWeight: 500 }}>No open pay period found.</Typography>
              )}
            </Stack>
            {access?.role === 'manager' ? (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Manager scope: view and approve only assigned location(s).
              </Typography>
            ) : null}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mt: 1.5, maxWidth: 420 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Pay Period</InputLabel>
                <Select
                  value={selectedPayPeriodId || ''}
                  label="Pay Period"
                  onChange={handleSelectPayPeriod}
                  disabled={switchingPeriod}
                >
                  {payPeriods.map((period) => (
                    <MenuItem key={period.id} value={period.id}>
                      {`${formatDate(period.startDate)} - ${formatDate(period.endDate)}${String(period.status || '').toLowerCase() === 'exported' ? ' (EXPORTED)' : ''}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            {switchingPeriod ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  Loading pay period...
                </Typography>
              </Stack>
            ) : null}
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="contained"
              color="success"
              startIcon={activeAction === 'approve' ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
              onClick={handleApprove}
              disabled={!canApprove || submitting}
              sx={{ minHeight: 42, px: 2.2, fontWeight: 700, borderRadius: 2 }}
            >
              {activeAction === 'approve' ? 'Approving...' : 'Approve Payroll'}
            </Button>
            <Button
              variant="contained"
              startIcon={activeAction === 'export' ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
              onClick={() => handleExport()}
              disabled={!canExport || submitting}
              sx={{ minHeight: 42, px: 2.2, fontWeight: 700, borderRadius: 2 }}
            >
              {activeAction === 'export' ? 'Exporting...' : 'Export CSV'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {submitting && activeAction ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {activeAction === 'approve'
            ? 'Approving payroll. This can take a few seconds for period locking and recalculation.'
            : 'Exporting payroll CSV. This can take a few seconds.'}
        </Alert>
      ) : null}
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {info ? <Alert severity="success" sx={{ mb: 2 }}>{info}</Alert> : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0,1fr))', lg: 'repeat(5, minmax(0,1fr))' },
          gap: 1.5,
          mb: 2.5,
        }}
      >
        {statTiles.map((tile) => (
          <StatTile
            key={tile.label}
            icon={tile.icon}
            label={tile.label}
            value={tile.value}
            accent={tile.accent}
          />
        ))}
      </Box>

      <Box sx={{ mb: 2.5 }}>
        {payPeriod?.status ? (
          <Stack direction="row" spacing={1}>
            <Chip
              label={`Period Status: ${String(payPeriod.status).toUpperCase()}`}
              color={payPeriod.status === 'exported' ? 'success' : (payPeriod.status === 'approved' ? 'primary' : 'default')}
              sx={{ fontWeight: 700, borderRadius: 1.5, px: 0.5 }}
            />
            <Chip
              label={selectedPayPeriodId === payPeriod?.id ? 'Viewing: Selected' : 'Viewing: Current'}
              variant="outlined"
              sx={{ fontWeight: 600, borderRadius: 1.5, px: 0.5 }}
            />
          </Stack>
        ) : null}
      </Box>

      <Paper sx={{ p: 2.2, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h5" sx={{ mb: 1.2, fontWeight: 700 }}>Payroll Issues</Typography>
        {payrollIssues.length === 0 ? (
          <Alert severity="success" sx={{ borderRadius: 2 }}>No blocking payroll issues found.</Alert>
        ) : (
          <Stack spacing={1}>
            {payrollIssues.map((issue, idx) => (
              <Alert
                key={`${issue.timecardId || 'x'}_${idx}`}
                severity={issue.severity === 'error' ? 'error' : 'warning'}
                icon={<WarningIcon />}
                sx={{ borderRadius: 2 }}
              >
                {issue.message}
              </Alert>
            ))}
          </Stack>
        )}
      </Paper>

      <Paper sx={{ overflow: 'hidden', mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ p: 2.2, pb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Employee Totals</Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#eef2f6' }}>
              <TableCell sx={{ fontWeight: 700 }}>Employee ID</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Employee Name</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Earning Code</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Regular Hours</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Overtime Hours</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Total Hours</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(summary?.employees || []).map((row) => (
              <TableRow key={`${row.employeeId || 'unknown'}_${row.rolePayrollCode || 'role'}`} hover>
                <TableCell>{row.payrollEmployeeId || '-'}</TableCell>
                <TableCell>{row.employeeName || 'Unknown Employee'}</TableCell>
                <TableCell>{row.roleName || '-'}</TableCell>
                <TableCell>{row.rolePayrollCode || '-'}</TableCell>
                <TableCell align="right">{formatHours(row.regularMinutes)}</TableCell>
                <TableCell align="right">{formatHours(row.overtimeMinutes)}</TableCell>
                <TableCell align="right">{formatHours(row.totalWorkedMinutes)}</TableCell>
              </TableRow>
            ))}
            {(summary?.employees || []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ py: 4 }}>
                  <Stack alignItems="center" spacing={0.8}>
                    <ReceiptLongIcon sx={{ color: 'text.disabled' }} />
                    <Typography color="text.secondary">No timecards in this pay period yet.</Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Paper>

      <Paper sx={{ p: 2.2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.2 }}>
          <HistoryIcon sx={{ color: 'text.secondary' }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Payroll History</Typography>
        </Stack>
        {history.length === 0 ? (
          <Typography color="text.secondary">No approved/exported payroll periods yet.</Typography>
        ) : (
          <Stack spacing={1.1}>
            {history.map((period) => (
              <Stack
                key={period.id}
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                spacing={1}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 1.4,
                  background: '#f2f5f8',
                }}
              >
                <Typography sx={{ fontWeight: 600 }}>
                  {formatDate(period.startDate)} - {formatDate(period.endDate)}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" label={period.status || 'unknown'} color={period.status === 'exported' ? 'success' : 'default'} />
                  {period.exportedAt ? <Typography variant="caption" color="text.secondary">Exported {formatDate(period.exportedAt)}</Typography> : null}
                  {(period.status === 'approved' || period.status === 'exported') ? (
                    <Button size="small" variant="outlined" onClick={() => handleExport(period.id)} disabled={submitting}>
                      Download CSV
                    </Button>
                  ) : null}
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}
      </Paper>
      </Container>
    </Box>
  );
}
